<script setup lang="ts">
import { computed } from 'vue';
import { RenderNodes, useTruncationTitle, wrapTextRuns } from '../utils/truncation-title';

const props = withDefaults(
  defineProps<{
    /** `attention` is coral (spec §2): unread counts, "Neu" — it shows, it does not judge. */
    tone?: 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'info' | 'attention';
    dot?: boolean;
    /** The dot's colour (a token or CSS colour) when it is not the text colour — e.g. a
     * project's colour. Needs `dot`. */
    dotColor?: string;
    /** A pulsing dot: something is running right now (a clock that is ticking). The
     * pulse stops under `prefers-reduced-motion`; the dot stays. Needs `dot`. */
    live?: boolean;
    /** Opt-in for free-text content (e.g. skill tags) that can exceed the container width. */
    wrap?: boolean;
    /** Solid fill instead of the soft pastel pairing — same tone tokens, inverted,
     * for a status indicator that has to read at a glance (issue 175) rather than
     * sit quietly (the default soft look, still right for tags/counts/filters). */
    solid?: boolean;
    /** The pill is (or sits in) the thing that gets clicked — a button or link
     * around it, or a click on the pill itself. It then says so: pointer cursor,
     * and the pill's edge takes its text colour on hover and keyboard focus. A
     * pill that merely labels leaves this off and stays exactly as it was. */
    interactive?: boolean;
  }>(),
  { tone: 'neutral', dot: false, dotColor: undefined, live: false, wrap: false, solid: false, interactive: false },
);

// The tone classes live in the style block: soft pairs each functional tint with
// its own text colour, solid pairs the solid with its `-fg` (spec §2: lime and
// ochre carry espresso text, the darker tones white — `--text-inverse` would be
// wrong on half of them).
// A pill that does not fit its column ellipsises its text (never the pill itself),
// and the full text is then the native tooltip — unless the caller set a `title`.
// Only the slot's text is wrapped for that: a dot or icon the caller puts in the
// slot stays a flex item of the pill, with its size, the gap and the centring.
const { rootEl, title: autoTitle, reveal } = useTruncationTitle('lumi-badge__text');

const toneClass = computed(() => [
  `lumi-badge--${props.tone}`,
  { 'lumi-badge--solid': props.solid, 'lumi-badge--wrap': props.wrap, 'lumi-badge--interactive': props.interactive },
]);
</script>

<template>
  <span ref="rootEl" class="lumi-badge" :class="toneClass" :title="autoTitle" @mouseenter="reveal">
    <span
      v-if="dot"
      class="lumi-badge__dot"
      :class="{ 'lumi-badge__dot--live': live }"
      :style="dotColor ? { background: dotColor } : undefined"
      aria-hidden="true"
    ></span>
    <RenderNodes :nodes="wrapTextRuns($slots.default?.() ?? [], 'lumi-badge__text')" />
  </span>
</template>

<style>
/* Spec §6: 999 px pills at 12 px; functional soft + text colours; the neutral
   pill sits on wash with the neutral pill ring. */
.lumi-badge{display:inline-flex;align-items:center;gap:6px;padding:2px 10px;border:1px solid transparent;border-radius:var(--radius-pill);font-family:var(--font-body);font-size:var(--text-xs);font-weight:var(--weight-medium);line-height:1.5;white-space:nowrap;max-width:100%;min-width:0;overflow:hidden;flex-shrink:0;}
/* The text is what gives way in a narrow column: the pill keeps its shape and
   is capped at the column (max-width), the label ends in an ellipsis. In a flex
   row a pill does NOT shrink by default — a count next to an ellipsising label
   must stay readable; a row that wants a pill to give way opts in with
   flex-shrink:1 on it. */
.lumi-badge__text{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:inherit;}
.lumi-badge--wrap{white-space:normal;overflow-wrap:anywhere;}
.lumi-badge--wrap .lumi-badge__text{overflow:visible;text-overflow:clip;}
.lumi-badge__dot{width:6px;height:6px;border-radius:50%;background:currentColor;flex-shrink:0;}
.lumi-badge__dot--live{animation:lumi-badge-pulse 1.6s ease-in-out infinite;}
@keyframes lumi-badge-pulse{0%,100%{opacity:1;}50%{opacity:.35;}}
@media (prefers-reduced-motion:reduce){.lumi-badge__dot--live{animation:none;}}
.lumi-badge--neutral{background:var(--surface-wash);border-color:var(--pill-ring);color:var(--text-body);}
.lumi-badge--accent{background:var(--surface-wash);border-color:var(--pill-ring);color:var(--text-heading);}
.lumi-badge--success{background:var(--success-soft);color:var(--success-text);}
.lumi-badge--warning{background:var(--warning-soft);color:var(--warning-text);}
.lumi-badge--danger{background:var(--danger-soft);color:var(--danger-text);}
.lumi-badge--info{background:var(--info-soft);color:var(--info-text);}
.lumi-badge--attention{background:var(--attention-soft);color:var(--attention-text);}
.lumi-badge--solid{border-color:transparent;}
/* Clickable pills: the edge takes the text colour (every tone has one, so the
   cue holds on soft and solid alike), under the pointer — or under the pointer on
   the button/link the pill sits in — and, for a pill that is focusable itself,
   the shared focus ring. The wrapping button keeps its own focus ring. */
.lumi-badge--interactive{cursor:pointer;transition:border-color var(--dur-fast) var(--ease-standard);}
.lumi-badge--interactive:hover,
:is(button,a,[role="button"]):not(:disabled):hover>.lumi-badge--interactive{border-color:currentColor;}
.lumi-badge--interactive:focus-visible{outline:2px solid var(--focus-ring);outline-offset:2px;border-color:currentColor;}
.lumi-badge--solid.lumi-badge--neutral{background:var(--text-heading);color:var(--text-inverse);}
.lumi-badge--solid.lumi-badge--accent{background:var(--btn-primary-bg);color:var(--btn-primary-fg);}
.lumi-badge--solid.lumi-badge--success{background:var(--success);color:var(--success-fg);}
.lumi-badge--solid.lumi-badge--warning{background:var(--warning);color:var(--warning-fg);}
.lumi-badge--solid.lumi-badge--danger{background:var(--danger);color:var(--danger-fg);}
.lumi-badge--solid.lumi-badge--info{background:var(--info);color:var(--info-fg);}
.lumi-badge--solid.lumi-badge--attention{background:var(--attention);color:var(--attention-fg);}
</style>
